import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { notificationQueryKeys } from "hooks/queries/useNotificationQueries";

interface NotificationBroadcastMessage {
  type: "notification:new" | "notification:count";
  count?: number;
  payload?: Record<string, unknown>;
}

/**
 * Listen for cross-tab notification state changes via the BroadcastChannel API.
 *
 * When one tab receives a WebSocket `notification:new` or `notification:count`
 * event, it posts to a shared `BroadcastChannel("eoe-notifications")`. Every
 * other tab runs this hook to pick up those messages and update its TanStack
 * Query cache so that badge counts stay in sync across all open tabs.
 *
 * This hook is a no-op if BroadcastChannel is unavailable (e.g. in some
 * WebView / older browser contexts).
 */
export function useNotificationBroadcast(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    let channel: BroadcastChannel | null = null;

    try {
      channel = new BroadcastChannel("eoe-notifications");
    } catch {
      return;
    }

    const handleMessage = (event: MessageEvent<NotificationBroadcastMessage>) => {
      const data = event.data;
      if (!data || typeof data !== "object") {
        return;
      }

      switch (data.type) {
        case "notification:count":
          if (typeof data.count === "number") {
            queryClient.setQueryData<{ count: number }>(
              notificationQueryKeys.unreadCount(),
              { count: data.count }
            );
          }
          break;

        case "notification:new":
          queryClient.setQueryData<{ count: number }>(
            notificationQueryKeys.unreadCount(),
            (prev) => ({ count: (prev?.count ?? 0) + 1 })
          );
          void queryClient.invalidateQueries({
            queryKey: notificationQueryKeys.list(),
          });
          break;

        default:
          break;
      }
    };

    channel.addEventListener("message", handleMessage);

    return () => {
      channel?.removeEventListener("message", handleMessage);
      channel?.close();
    };
  }, [queryClient]);
}
